import { Box, Link as MaterialLink } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import ClaimsApi from '../../api/ClaimsApi';
import { handleUnauthorizedResponse } from '../../api/http';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { PolicyContext, PolicyData } from '../../context/policy';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../common';
import LargeCenterSpinner from '../LargeCenterSpinner';
import FieldEditorForm from '../policy/FieldEditorForm';
import { errorToast } from '../Toast';
import SubclaimLedger from './SubclaimLedger';
import SubclaimSummaryTable from './SubclaimSummaryTable';

interface Props {
	claimData: ClaimData;
	authData: AuthData;
	policyData: PolicyData;
}

const useStyles = makeStyles(() => ({
	linkWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
	},
}));

const Subclaim = (props: Props) => {
	const classes = useStyles();
	const { claimData, authData, policyData } = props;
	const { hasPermission } = authData;
	const { productConfig, policy } = policyData;
	const { claim } = claimData;
	const params = useParams();
	const { subclaimId } = params;
	const [summary, setSummary] = useState(null);
	const [loading, setLoading] = useState(true);
	const initialSubclaim = claim.subClaims.find(
		(sC) => sC.locator === subclaimId,
	);
	const [subclaim, setSubclaim] = useState(initialSubclaim);
	const { claimConfiguration } = productConfig;

	useEffect(() => {
		const fetchSummary = async () => {
			try {
				const subClaimSummary = await ClaimsApi.fetchSubclaimSummary(
					authData.auth.authorizationToken,
					subclaimId,
				);
				setSummary(subClaimSummary.data);
				setLoading(false);
			} catch (error) {
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
				errorToast('Failed to fetch subclaim summary');
			}
		};
		fetchSummary();
		setSubclaim(claim.subClaims.find((sC) => sC.locator === subclaimId));
	}, [claim, subclaimId]);

	if (loading) {
		return <LargeCenterSpinner />;
	}

	return (
		<>
			<div className={classes.linkWrapper}>
				{hasPermission('auxData.fetch') && (
					<MaterialLink component={RouterLink} to='auxData'>
						Aux Data
					</MaterialLink>
				)}
			</div>
			<Box sx={{ mb: 2, mt: 2 }}>
				<StyledTitle>Subclaim Status: {subclaim.currentStatus}</StyledTitle>
				{claimConfiguration?.subClaimConfiguration?.fields && (
					<FieldEditorForm
						isEditable={false}
						currentFieldValues={subclaim.fieldValues}
						fieldValues={subclaim.fieldValues}
						fieldGroupsByLocator={subclaim.fieldGroupsByLocator}
						fieldConfigurations={
							claimConfiguration.subClaimConfiguration.fields
						}
						updateFieldValue={null}
						onAddFieldGroup={null}
						removeFieldGroup={null}
						updateGroupFieldValue={null}
						mediaByLocator={null}
						configVersion={productConfig.configVersion}
					/>
				)}
			</Box>
			<hr />
			<StyledSectionContainer>
				<SubclaimSummaryTable subClaimSummary={summary} />
			</StyledSectionContainer>
			<StyledTitleContainer>
				<StyledTitle>Ledgers</StyledTitle>
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<SubclaimLedger
					ledgerEntries={subclaim.lossLedger}
					ledgerTitle={'Loss'}
					currency={policy?.currency}
				/>
				<SubclaimLedger
					ledgerEntries={subclaim.expenseLedger}
					ledgerTitle={'Expense'}
					currency={policy?.currency}
				/>
			</StyledSectionContainer>
		</>
	);
};

function withContext<T>(Component: any) {
	return (props: Omit<T, 'claimData' | 'authData' | 'policyData'>) => (
		<ClaimContext.Consumer>
			{(claimData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<AuthContext.Consumer>
							{(authData) => (
								<Component
									{...props}
									claimData={claimData}
									policyData={policyData}
									authData={authData}
								/>
							)}
						</AuthContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</ClaimContext.Consumer>
	);
}

export default withContext(Subclaim);
